import { Card, CardProps } from '@unistocks/components/layout/card';
import { SavingsGoalParagraph as Paragraph } from '@unistocks/features/savings-goals/components/cards/savings-goal-paragraph';
import { SavingsGoalProps } from '@unistocks/features/savings-goals/components/savings-goal-props';
import { SavingsGoalDataPoint } from '@unistocks/proxy/axios-client';
import {
  formatCurrency,
  formatDate,
  formatPercentage,
  t
} from '@unistocks/shared/localization/index.ts';
import { createDate, today } from '@unistocks/shared/utils/util';
import { memo } from 'react';
import { ParagraphProps } from 'tamagui';

export const SavingsGoalStatus = memo<SavingsGoalProps & CardProps>(
  ({ savingsGoal, goalCalculation: { goal, expected, progress }, ...props }) => {
    const endDate = createDate(savingsGoal.endDate);
    const formattedGoalValue = formatCurrency(savingsGoal.goalValue, savingsGoal.currency.code);
    const formattedGoalEndDate = formatDate(savingsGoal.endDate, { dateStyle: 'long' });

    const goalReachedDate =
      progress.find(({ type }) => type === SavingsGoalDataPoint.GoalValueReached) ??
      expected.find(({ type }) => type === SavingsGoalDataPoint.GoalValueReached);

    const isPastGoalValue = goalReachedDate && today() > createDate(goalReachedDate.timestamp);
    const isPastGoalDate = today() > endDate;

    const goalEndDateValue = goal.find(({ type }) => type === SavingsGoalDataPoint.EndDate)?.value;
    const actualEndDateValue = isPastGoalDate
      ? progress.find(({ type }) => type === SavingsGoalDataPoint.EndDate)?.value
      : expected.find(({ type }) => type === SavingsGoalDataPoint.EndDate)?.value;

    let goalValueStatusText = '',
      goalReachedDateText = '',
      formattedGoalReachedDate = '',
      formattedGoalReachedEndDateDiff = '',
      goalReachedEndDateDiffColor: ParagraphProps['color'],
      goalReachedEndDateDiffDesc = '';

    let goalDateStatusText = '',
      formattedEndDateValue = '',
      goalReachedValueText = '';

    let actualValueGoalDiffPercentage = '',
      actualValueGoalDiffNominal = '',
      actualValueGoalDiffColor: ParagraphProps['color'],
      actualValueGoalDiffDesc = '';

    let actualValueCalcDiffPercentage = '',
      actualValueCalcDiffNominal = '',
      actualValueCalcDiffDiffColor: ParagraphProps['color'],
      actualValueCalcDiffDesc = '';

    /* Text comparing the portfolio value to the savings goal value on the expected date when the goal will be reached */
    if (goalReachedDate) {
      goalValueStatusText = t('::SavingsGoal:Status:GoalValue');
      goalReachedDateText = t(
        isPastGoalValue
          ? '::SavingsGoal:Status:GoalValuePast'
          : '::SavingsGoal:Status:GoalValueFuture'
      );
      formattedGoalReachedDate = formatDate(goalReachedDate.timestamp, { dateStyle: 'long' });

      const goalReachedEndDateDiff = createDate(goalReachedDate.timestamp).diff(
        createDate(savingsGoal.endDate)
      );

      if (goalReachedEndDateDiff.as('milliseconds') <= 0) {
        goalReachedEndDateDiffColor = '$positive';
      } else {
        goalReachedEndDateDiffColor = '$negative';
      }

      if (goalReachedEndDateDiff.as('days') < 31) {
        formattedGoalReachedEndDateDiff = '';
      } else if (goalReachedEndDateDiff.as('years') >= 1) {
        formattedGoalReachedEndDateDiff = goalReachedEndDateDiff.toFormat(
          `y '${t('::Years').toLocaleLowerCase()}' M '${t('::Months').toLocaleLowerCase()}'` //  d '${t('::Days').toLocaleLowerCase()}'
        );
      } else if (goalReachedEndDateDiff.as('months') >= 1) {
        formattedGoalReachedEndDateDiff = goalReachedEndDateDiff.toFormat(
          `M '${t('::Months').toLocaleLowerCase()}'` //  d '${t('::Days').toLocaleLowerCase()}'
        );
      } else {
        formattedGoalReachedEndDateDiff = goalReachedEndDateDiff.toFormat(
          `d '${t('::Days').toLocaleLowerCase()}'`
        );
      }

      goalReachedEndDateDiffDesc = formattedGoalReachedEndDateDiff
        ? goalReachedEndDateDiff.as('milliseconds') <= 0
          ? t('::SavingsGoal:Status:TimeBeforeGoalDate')
          : t('::SavingsGoal:Status:TimeAfterGoalDate')
        : t('::SavingsGoal:Status:TimeOnGoalDate');
    }

    /* Text comparing the portfolio value to the calculated goal value at the savings goal end date */
    if (actualEndDateValue && goalEndDateValue) {
      goalDateStatusText = t('::SavingsGoal:Status:GoalDate');
      formattedEndDateValue = formatCurrency(actualEndDateValue, savingsGoal.currency.code, {
        maximumFractionDigits: 0
      });

      goalReachedValueText = t(
        isPastGoalDate ? '::SavingsGoal:Status:GoalDatePast' : '::SavingsGoal:Status:GoalDateFuture'
      );

      /* The difference between the actual/expected value and the value set in the goal */
      const actualValueGoalDiff = actualEndDateValue - savingsGoal.goalValue;
      actualValueGoalDiffPercentage = formatPercentage(
        Math.abs(actualValueGoalDiff / savingsGoal.goalValue)
      );
      actualValueGoalDiffNominal = formatCurrency(
        Math.abs(actualValueGoalDiff),
        savingsGoal.currency.code,
        { maximumFractionDigits: 0 }
      );
      actualValueGoalDiffColor = actualValueGoalDiff >= 0 ? '$positive' : '$negative';

      /* The difference between the actual/expected value and the value calculated by the goal settings on the goal end date */
      const actualValueCalcDiff = actualEndDateValue - goalEndDateValue;
      actualValueCalcDiffPercentage = formatPercentage(
        Math.abs(actualValueCalcDiff / goalEndDateValue)
      );
      actualValueCalcDiffNominal = formatCurrency(
        Math.abs(actualValueCalcDiff),
        savingsGoal.currency.code,
        {
          maximumFractionDigits: 0
        }
      );
      actualValueGoalDiffDesc = t(
        actualEndDateValue >= savingsGoal.goalValue
          ? '::SavingsGoal:Status:PercentageOverGoalValue'
          : '::SavingsGoal:Status:PercentageUnderGoalValue'
      );
      actualValueCalcDiffDiffColor = actualValueCalcDiff >= 0 ? '$positive' : '$negative';

      actualValueCalcDiffDesc = t(
        actualEndDateValue >= goalEndDateValue
          ? '::SavingsGoal:Status:NominalDiffFromGoalDatePositive'
          : '::SavingsGoal:Status:NominalDiffFromGoalDateNegative'
      );
    }

    return (
      <Card {...props}>
        <Card.Header>
          <Card.Heading>{t('::SavingsGoal:Status:Title')}</Card.Heading>
        </Card.Header>
        <Card.Body>
          <Paragraph>
            {goalValueStatusText} <Paragraph fontWeight={'$7'}>{formattedGoalValue}</Paragraph>{' '}
            <Paragraph>{goalReachedDateText}</Paragraph>{' '}
            <Paragraph fontWeight={'$7'}>{formattedGoalReachedDate}</Paragraph>.{' '}
            {formattedGoalReachedEndDateDiff ? (
              <>
                <Paragraph color={goalReachedEndDateDiffColor} fontWeight={'$7'}>
                  {formattedGoalReachedEndDateDiff}
                </Paragraph>{' '}
              </>
            ) : null}
            <Paragraph>{goalReachedEndDateDiffDesc}</Paragraph>.
          </Paragraph>

          {goalDateStatusText ? (
            <Paragraph whiteSpace="break-spaces">
              {goalDateStatusText} <Paragraph fontWeight={'$7'}>{formattedGoalEndDate}</Paragraph>{' '}
              <Paragraph>{goalReachedValueText}</Paragraph>{' '}
              <Paragraph fontWeight={'$7'}>{formattedEndDateValue}</Paragraph>.{'\n'}
              <Paragraph color={actualValueGoalDiffColor} fontWeight={'$7'}>
                {actualValueGoalDiffNominal} ({actualValueGoalDiffPercentage})
              </Paragraph>{' '}
              <Paragraph>{actualValueGoalDiffDesc}</Paragraph>
              {'\n'}
              <Paragraph color={actualValueCalcDiffDiffColor} fontWeight={'$7'}>
                {actualValueCalcDiffNominal} ({actualValueCalcDiffPercentage})
              </Paragraph>{' '}
              <Paragraph>{actualValueCalcDiffDesc}</Paragraph>
            </Paragraph>
          ) : null}
        </Card.Body>
      </Card>
    );
  }
);

SavingsGoalStatus.displayName = 'SavingsGoalStatus';
